// Which piece set the game draws, as a setting rather than a link.
//
// A pack was a developer toggle: `?pack=` to look at a build, and the dev menu
// to switch one mid-game. Both are still there and both are still the right
// shape for what they are for — a URL is something you hand somebody, and the
// dev menu shows every pack including the ones in progress. Neither is a thing
// a player can *choose and keep*, which is what art the game ships with needs.
//
// So this is the player-facing half: the finished sets, remembered across
// sessions. It is allowed to be narrower than `PIECE_PACKS` — a set in
// progress belongs in the dev menu, not here.

import { useCallback, useState } from "react";

import { PIECE_PACKS, type PiecePack } from "src/client/ai/render/models";

/**
 * The packs a player may pick, in menu order.
 *
 * **Its own list rather than `PIECE_PACKS`**, which is every pack the loader
 * knows. Offering a player a half-set is offering them a board with base
 * pawns standing in for the ranks it lacks, which reads as the game being
 * broken rather than as art in progress; the dev menu is where everything is
 * visible. The two are equal today.
 */
export const PACK_CHOICES: readonly { id: PiecePack; name: string; detail: string }[] = [
  {
    id: "base",
    name: "Walkers",
    detail: "The machines. One body per rank, in each army's colour."
  },
  {
    id: "classic",
    name: "Classic",
    detail: "The carved set. One silhouette per rank, in each army's colour."
  }
];

export const DEFAULT_PACK: PiecePack = "base";

const KEY = "chessfactory.pack";

/**
 * The stored choice, or the default on anything at all.
 *
 * Anything: no storage (a private window refuses it and *throws* on access), a
 * pack that has since been renamed, a half-written string. **Validated against
 * `PIECE_PACKS` rather than trusted**, for the reason `packFromUrl` is: a
 * stale name should boot the classic set, not an empty board.
 */
export function loadPack(): PiecePack {
  try {
    const raw = window.localStorage?.getItem(KEY);
    return PIECE_PACKS.includes(raw as PiecePack) ? (raw as PiecePack) : DEFAULT_PACK;
  } catch {
    return DEFAULT_PACK;
  }
}

export function savePack(pack: PiecePack): void {
  try {
    window.localStorage?.setItem(KEY, pack);
  } catch {
    // A setting that cannot be remembered still has to work for this session.
  }
}

/** The choice, held in state and remembered across sessions. */
export function usePackPref(): [PiecePack, (pack: PiecePack) => void] {
  const [pack, setPack] = useState<PiecePack>(loadPack);
  const set = useCallback((next: PiecePack) => {
    savePack(next);
    setPack(next);
  }, []);
  return [pack, set];
}
